import { useState } from "react";
import { Plus } from "lucide-react";
import { SettingsPage } from "@/components/v2/settings-page";
import { SettingsField } from "@/components/v2/settings-field";
import { SettingsTable } from "@/components/v2/settings-table";
import { FormSheetFooter } from "@/components/v2/form-sheet-footer";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { QueryError } from "@/components/query-error";
import { CopyId } from "@/components/v2/copy-id";
import {
  useBrands,
  useCreateBrand,
  useUpdateBrand,
  useDeactivateBrand,
  type Brand,
  type CreateBrandInput,
} from "@/hooks/use-brands";

const CHANNEL_TYPE_LABELS: Record<string, string> = {
  direct: "Direct",
  retail_partner: "Retail Partner",
  wholesale: "Wholesale",
};

function getBrandInitials(name: string): string {
  return name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

// ─── Brand Sheet ──────────────────────────────────────────────────────────────

interface BrandSheetProps {
  open: boolean;
  onClose: () => void;
  brand?: Brand;
}

interface BrandFormState {
  name: string;
  short_code: string;
  channel_type: string;
  partner_name: string;
}

const emptyForm: BrandFormState = {
  name: "",
  short_code: "",
  channel_type: "",
  partner_name: "",
};

function brandToForm(brand: Brand): BrandFormState {
  return {
    name: brand.name,
    short_code: brand.short_code ?? "",
    channel_type: brand.channel_type ?? "",
    partner_name: brand.partner_name ?? "",
  };
}

function BrandSheet({ open, onClose, brand }: BrandSheetProps) {
  const isEdit = Boolean(brand);
  const [form, setForm] = useState<BrandFormState>(
    brand ? brandToForm(brand) : emptyForm,
  );
  const [error, setError] = useState<string | null>(null);
  const createBrand = useCreateBrand();
  const updateBrand = useUpdateBrand();
  const deactivateBrand = useDeactivateBrand();

  function patch(fields: Partial<BrandFormState>) {
    setForm((prev) => ({ ...prev, ...fields }));
  }

  function handleClose() {
    setForm(brand ? brandToForm(brand) : emptyForm);
    setError(null);
    onClose();
  }

  async function handleSave() {
    if (!form.name.trim()) {
      setError("Name is required");
      return;
    }
    setError(null);

    const payload: CreateBrandInput = {
      name: form.name.trim(),
      short_code: form.short_code.trim() || undefined,
      channel_type:
        (form.channel_type as CreateBrandInput["channel_type"]) || undefined,
      partner_name: form.partner_name.trim() || undefined,
    };

    try {
      if (isEdit && brand) {
        await updateBrand.mutateAsync({ id: brand.id, ...payload });
      } else {
        await createBrand.mutateAsync(payload);
      }
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save brand");
    }
  }

  async function handleDeactivate() {
    if (!brand) return;
    try {
      await deactivateBrand.mutateAsync(brand.id);
      handleClose();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to deactivate brand",
      );
    }
  }

  const isPending =
    createBrand.isPending || updateBrand.isPending || deactivateBrand.isPending;

  return (
    <Sheet open={open} onOpenChange={(isOpen) => !isOpen && handleClose()}>
      <SheetContent className="w-[440px] sm:max-w-[440px] flex flex-col">
        <SheetHeader>
          <SheetTitle>{isEdit ? "Edit Brand" : "New Brand"}</SheetTitle>
        </SheetHeader>

        <form
          id="brand-form"
          className="flex-1 overflow-y-auto py-4 space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            handleSave();
          }}
        >
          <SettingsField label="Name">
            <Input
              value={form.name}
              onChange={(e) => patch({ name: e.target.value })}
              placeholder="Canopy Direct"
            />
          </SettingsField>

          <SettingsField label="Short Code" description="Optional">
            <Input
              value={form.short_code}
              onChange={(e) => patch({ short_code: e.target.value })}
              placeholder="CD"
              className="font-mono uppercase"
            />
          </SettingsField>

          <SettingsField label="Channel Type" description="Optional">
            <Select
              value={form.channel_type}
              onValueChange={(val) => patch({ channel_type: val })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select channel type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="direct">Direct</SelectItem>
                <SelectItem value="retail_partner">Retail Partner</SelectItem>
                <SelectItem value="wholesale">Wholesale</SelectItem>
              </SelectContent>
            </Select>
          </SettingsField>

          <SettingsField label="Partner Name" description="Optional">
            <Input
              value={form.partner_name}
              onChange={(e) => patch({ partner_name: e.target.value })}
              placeholder="e.g. Costco"
            />
          </SettingsField>

          {error && <p className="text-sm text-destructive">{error}</p>}

          {isEdit && (
            <Button
              type="button"
              variant="destructive"
              onClick={handleDeactivate}
              disabled={isPending}
              className="w-full sm:w-auto"
            >
              Deactivate
            </Button>
          )}
        </form>

        <FormSheetFooter
          onCancel={handleClose}
          submitLabel="Save Brand"
          isSubmitting={isPending}
          formId="brand-form"
        />
      </SheetContent>
    </Sheet>
  );
}

// ─── Brands Page ──────────────────────────────────────────────────────────────

export default function V2SettingsBrandsPage() {
  const { data, isLoading, error } = useBrands();
  const [sheetOpen, setSheetOpen] = useState(false);
  const [editBrand, setEditBrand] = useState<Brand | undefined>(undefined);

  const brands = data?.brands ?? [];

  function openCreate() {
    setEditBrand(undefined);
    setSheetOpen(true);
  }

  function openEdit(brand: Brand) {
    setEditBrand(brand);
    setSheetOpen(true);
  }

  if (error) {
    return <QueryError message="Failed to load brands" />;
  }

  return (
    <SettingsPage
      title="Brands"
      subtitle="Manage the brands and channel identities for this business."
      actions={
        <Button size="sm" onClick={openCreate}>
          <Plus className="mr-2 size-4" />
          New Brand
        </Button>
      }
    >
      <SettingsTable
        colSpan={7}
        isLoading={isLoading}
        isEmpty={brands.length === 0}
        emptyMessage="No brands yet. Create one to get started."
      >
        <TableHeader>
          <TableRow>
            <TableHead className="w-12" aria-label="Icon" />
            <TableHead>Name</TableHead>
            <TableHead>Short Code</TableHead>
            <TableHead>Brand ID</TableHead>
            <TableHead>Channel</TableHead>
            <TableHead>Partner</TableHead>
            <TableHead>Status</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {brands.map((brand) => {
            const iconSrc = brand.icon_url ?? brand.logo_url ?? undefined;
            return (
              <TableRow
                key={brand.id}
                className="cursor-pointer"
                onClick={() => openEdit(brand)}
              >
                <TableCell>
                  <Avatar className="size-6 rounded-sm">
                    {iconSrc ? <AvatarImage src={iconSrc} alt="" /> : null}
                    <AvatarFallback className="rounded-sm bg-[color:var(--color-accent)] text-[10px] font-medium text-white">
                      {getBrandInitials(brand.name)}
                    </AvatarFallback>
                  </Avatar>
                </TableCell>
                <TableCell className="font-medium">{brand.name}</TableCell>
                <TableCell className="typo-mono text-[color:var(--color-text-md)]">
                  {brand.short_code ?? "—"}
                </TableCell>
                <TableCell
                  className="w-72"
                  onClick={(e) => e.stopPropagation()}
                >
                  <CopyId
                    variant="compact"
                    value={brand.id}
                    ariaLabel="Copy brand ID"
                  />
                </TableCell>
                <TableCell>
                  {brand.channel_type
                    ? (CHANNEL_TYPE_LABELS[brand.channel_type] ??
                      brand.channel_type)
                    : "—"}
                </TableCell>
                <TableCell className="text-[color:var(--color-text-md)]">
                  {brand.partner_name ?? "—"}
                </TableCell>
                <TableCell>
                  <Badge variant={brand.is_active ? "default" : "secondary"}>
                    {brand.is_active ? "Active" : "Inactive"}
                  </Badge>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </SettingsTable>

      <BrandSheet
        open={sheetOpen}
        onClose={() => setSheetOpen(false)}
        brand={editBrand}
      />
    </SettingsPage>
  );
}
